import { ApiEndpoints } from "@shared/api/api-endpoints";
import { communicationApiClient } from "@shared/api/clients/apiClientFactory";

export const uxWorkflowApi = {
  getAllSegments: async (utilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "segments",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("dropdown_only", true);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },
  getAllRecipientList: async (utilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "recipient-lists",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("dropdown_only", true);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },

  createWorkflow: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "workflow"
    );
    const response = await communicationApiClient.post(url, payload);
    return response.data || response.data.result;
  },

  updateWorkflow: async (payload: any, workflowId: string): Promise<any> => {
    const response = await communicationApiClient.patch(
      `workflow/${workflowId}/`,
      payload
    );
    return response.data.result || response.data;
  },

  getAllCommunicationTemplatesByChannel: async (
    utilityId: string,
    search?: string,
    channel?: number
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "communication-templates",
      (query) => {
        query.push("remote_utility_id", utilityId);
        if (search) {
          query.push("search_data", search);
        }
        if (channel) {
          query.push("channel", channel);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },
  getOnlyTemplatesByEmail: async (utilityId: string, search?: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "communication-templates",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("channel", 0);
        if (search) {
          query.push("search_data", search);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },
  getOnlyTemplatesByTextMessage: async (utilityId: string, search?: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "communication-templates",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("channel", 1);
        if (search) {
          query.push("search_data", search);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },

  getWorkflowDetails: async (utilityId: string, id: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `workflow/${id}/detail`,
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },
  getAllWorkflows: async (
    utilityId: string,
    page: number,
    limit: number,
    search?: string,
    audienceType?: number,
    actionType?: number,
    status?: number
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "workflow",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("page", page);
        query.push("limit", limit);
        if (search) {
          query.push("search_data", search);
        }
        if (audienceType) {
          query.push("audience_type", audienceType);
        }
        if (actionType) {
          query.push("action_type", actionType);
        }
        if (status) {
          query.push("status", status);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },

  reRunWorkflow: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `workflow/${payload.id}/re-run`
    );
    const response = await communicationApiClient.post(url, payload);
    return response.data || response.data.result;
  },
  pauseWorkflow: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `workflow/${payload.id}/pause`
    );
    const response = await communicationApiClient.post(url, payload);
    return response.data || response.data.result;
  },

  getAllRunHistory: async (
    utilityId: string,
    page: number,
    limit: number,
    search?: string,
    executionDateFrom?: string,
    executionDateTo?: string,
    channel?: string,
    audienceType?: string,
    actionType?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "run-history",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("page", page);
        query.push("limit", limit);
        if (search) {
          query.push("search_data", search);
        }
        if (executionDateFrom) {
          query.push("execution_date_from", executionDateFrom);
        }
        if (executionDateTo) {
          query.push("execution_date_to", executionDateTo);
        }
        if (channel) {
          query.push("channel", channel);
        }
        if (audienceType) {
          query.push("audience_type", audienceType);
        }
        if (actionType) {
          query.push("action_type", actionType);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },

  getRunHistorySummary: async (utilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `run-history/summary`,
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },

  getAllWorkflowsExecutionDetails: async (
    utilityId: string,
    workflowId: string,
    page: number,
    limit: number,
    search?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `workflow/execution_history/${workflowId}`,
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("page", page);
        query.push("limit", limit);
        if (search) {
          query.push("search_data", search);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },
  getWorkflowsExecutionDetails: async (
    utilityId: string,
    executionId: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `workflow/execution_history/${executionId}`,
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },
  getWorkflowsExecutionChannelSummary: async (
    utilityId: string,
    executionId: string,
    channel: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `workflow/execution_history/${executionId}/${channel}/summary`,
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },

  getWorkflowsExecutionAllChannelRecipients: async (
    utilityId: string,
    executionId: string,
    channel: string,
    page: number,
    limit: number,
    search?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `workflow/execution_history/${executionId}/${channel}/recipients`,
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("page", page);
        query.push("limit", limit);
        if (search) {
          query.push("search_data", search);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },
};
